import { useEffect, useState } from "react";
import api from "../api/axios";
import toast from "react-hot-toast";

export default function AdminNotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState("");

  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await api.get("/admin/notifications");
        setNotifications(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load notifications");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  const markRead = async (id) => {
    if (markingId) return;
    setMarkingId(id);
    try {
      await api.patch(`/admin/notifications/${id}/read`);
      setNotifications((prev) => prev.map((item) => (item._id === id ? { ...item, isRead: true } : item)));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to mark as read");
    } finally {
      setMarkingId("");
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 text-white">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <p className="text-sm uppercase tracking-[0.24em] text-cyan-300/80">Admin</p>
        <h1 className="mt-2 text-3xl font-bold text-white">Notifications</h1>
        <p className="mt-2 text-sm text-slate-400">
          Review platform notifications and mark processed alerts as read.
        </p>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-3 text-3xl font-bold text-white">{notifications.length}</p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Unread</p>
          <p className="mt-3 text-3xl font-bold text-amber-300">
            {notifications.filter((item) => !item.isRead).length}
          </p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Read</p>
          <p className="mt-3 text-3xl font-bold text-emerald-300">
            {notifications.filter((item) => item.isRead).length}
          </p>
        </div>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-4 md:p-6 backdrop-blur-xl">
        {loading && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-slate-300">
            Loading notifications...
          </div>
        )}

        {!loading && notifications.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center text-slate-400">
            No notifications found.
          </div>
        )}

        {!loading && notifications.length > 0 && (
          <div className="space-y-3">
            {notifications.map((n) => (
              <div
                key={n._id}
                className="rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:bg-white/[0.08]"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h3 className="text-sm font-semibold capitalize text-white">{n.type} notification</h3>
                    <p className="mt-2 text-sm text-slate-300">{n.message}</p>
                    <p className="mt-2 text-xs text-slate-500">{new Date(n.createdAt).toLocaleString()}</p>
                  </div>

                  <div className="flex items-center gap-2 md:flex-col md:items-end">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      n.isRead
                        ? "bg-emerald-500/15 text-emerald-300"
                        : "bg-amber-500/15 text-amber-300"
                    }`}>
                      {n.isRead ? "Read" : "Unread"}
                    </span>
                    {!n.isRead && (
                      <button
                        onClick={() => markRead(n._id)}
                        disabled={Boolean(markingId)}
                        aria-busy={markingId === n._id}
                        className="rounded-lg bg-cyan-300/15 px-3 py-1.5 text-xs font-semibold text-cyan-300 transition hover:bg-cyan-300/25 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {markingId === n._id ? "Marking…" : "Mark as read"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
